import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, RefreshCw, CalendarDays, Coins } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { money } from "../../lib/format";

const CATEGORIES = ["Housing", "Utilities", "Insurance", "Subscriptions", "Transport", "Loan", "Other"];

export default function BillsManager({ open, onOpenChange, month, currency, onChanged, readOnly }) {
  const [bills, setBills] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Housing");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");

  const guard = () => { if (readOnly) { toast.error("View-only account — editing is disabled."); return true; } return false; };

  const load = useCallback(async () => {
    const { data } = await api.get(`/bills?month=${month}`);
    const sorted = [...data].sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1; // undated bills go to the bottom
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date); // earliest due date first
    });
    setBills(sorted);
  }, [month]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    if (guard()) return;
    if (!name || !amount) return toast.error("Enter bill name and amount");
    await api.post("/bills", { name, category, amount: Number(amount), due_date: dueDate || null, month });
    setName(""); setAmount(""); setDueDate("");
    toast.success("Bill added");
    load(); onChanged();
  };

  const doPay = async (b) => {
    if (guard()) return;
    const amt = Number(payAmt);
    if (!amt || amt <= 0) return toast.error("Enter a payment amount");
    await api.post(`/bills/${b.id}/pay`, { amount: amt });
    setPayFor(null); setPayAmt("");
    const remaining = Number(b.amount) - Number(b.paid_amount || 0);
    toast.success(amt >= remaining ? "Bill fully paid" : "Partial payment recorded");
    load(); onChanged();
  };

  const del = async (id) => {
    if (guard()) return;
    await api.delete(`/bills/${id}`);
    load(); onChanged();
  };

  const carry = async () => {
    if (guard()) return;
    const { data } = await api.post(`/bills/carry-forward?month=${month}`);
    toast.success(`${data.created} unpaid bill(s) carried forward`);
    load(); onChanged();
  };

  const statusColor = { paid: "bg-emerald-100 text-emerald-700", partial: "bg-amber-100 text-amber-700", unpaid: "bg-red-100 text-red-700" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">Bills Wallet</DialogTitle>
          <DialogDescription>Add fixed bills, mark them paid, and carry unpaid ones into the next month.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 items-end bg-slate-50 p-3 rounded-xl sm:flex sm:flex-wrap">
          <div className="col-span-2 sm:flex-1 sm:min-w-[140px]">
            <Input data-testid="bill-name-input" placeholder="Bill name" value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
          </div>
          <Select value={category} onValueChange={setCategory} disabled={readOnly}>
            <SelectTrigger data-testid="bill-category-select" className="w-full sm:w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="bill-amount-input" type="number" placeholder="Amount" className="w-full sm:w-28 font-mono-nums" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={readOnly} />
          <Input data-testid="bill-due-input" type="date" className="w-full sm:w-40" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={readOnly} />
          <Button data-testid="bill-add-button" onClick={add} disabled={readOnly} className="col-span-2 sm:col-span-1 bg-orange-600 hover:bg-orange-700 text-white"><Plus className="w-4 h-4 sm:mr-0 mr-1" /><span className="sm:hidden">Add bill</span></Button>
        </div>

        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">{bills.length} bill(s) this month</p>
          <Button variant="outline" size="sm" onClick={carry} data-testid="bill-carry-button"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Carry unpaid forward</Button>
        </div>

        <div className="space-y-2">
          {bills.map((b) => (
            <div key={b.id} data-testid={`bill-row-${b.id}`} className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-slate-200">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-800 text-sm truncate">{b.name}</p>
                  {b.carried_forward && <Badge variant="outline" className="text-[10px]">carried</Badge>}
                </div>
                <p className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                  {b.category}{b.due_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{b.due_date}</span>}
                  {Number(b.paid_amount) > 0 && b.status !== "paid" && <span className="text-amber-600 font-medium">paid {money(b.paid_amount, currency)} of {money(b.amount, currency)}</span>}
                </p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${statusColor[b.status]}`}>{b.status}</span>
              <span className="font-mono-nums font-semibold text-slate-800 text-sm text-right">{money(b.amount, currency)}</span>
              {b.status !== "paid" && (payFor === b.id ? (
                <div className="flex items-center gap-1">
                  <Input autoFocus data-testid={`bill-pay-input-${b.id}`} type="number" className="w-24 h-8 font-mono-nums" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} />
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => doPay(b)} data-testid={`bill-pay-confirm-${b.id}`}>Pay</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setPayFor(b.id); setPayAmt(String(Number(b.amount) - Number(b.paid_amount || 0))); }} data-testid={`bill-pay-${b.id}`}>
                  <Coins className="w-3.5 h-3.5 mr-1" /> Pay
                </Button>
              ))}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(b.id)} data-testid={`bill-delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {bills.length === 0 && <p className="text-center text-sm text-slate-400 py-6">No bills yet. Add your first bill above.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
